import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { config, MARKETS } from '../config.ts';
import { fmtPx, fmtUsd } from '../lib/format.ts';
import { closedNet, type PositionView } from '../lib/positions.ts';
import type { Globals, Market } from '../lib/protocol.ts';
import type { ApiClosed } from '../lib/relayer.ts';
import { useApp } from '../state.tsx';
import { useDialogFocus, useEscape } from './ui.tsx';

/** What a PnL card shows: one position, open (live PnL at the mark) or closed */
export type PnlCardData = {
  market: number;
  isLong: boolean;
  leverage: bigint;
  entryPx: bigint;
  /** Exit price, or the mark of an open position */
  exitPx: bigint;
  priceScale: number;
  /** Net of fees, USD 18 dec */
  net: bigint;
  /** Net over the margin, percent */
  pct: number;
  status: 'open' | 'closed' | 'liquidated';
};

const scaleOf = (markets: Market[] | undefined, id: number) => markets?.find((m) => m.id === id)?.inst.priceScale ?? 10;

/** The card of a closed position */
export function cardFromClosed(h: ApiClosed, markets: Market[] | undefined, globals: Globals | undefined): PnlCardData {
  const net = closedNet(h, globals);
  return {
    market: h.instrumentId,
    isLong: h.isLong,
    leverage: h.leverage,
    entryPx: h.entryPx,
    exitPx: h.exitPx,
    priceScale: scaleOf(markets, h.instrumentId),
    net,
    pct: Number((net * 10_000n) / h.margin) / 100,
    status: h.outcome,
  };
}

/** The card of an open position at its mark; none while it has no quote */
export function cardFromOpen(v: PositionView, markets: Market[] | undefined): PnlCardData | undefined {
  if (v.net === undefined || v.mark === undefined || v.roe === undefined) return undefined;
  return {
    market: v.p.instrumentId,
    isLong: v.p.isLong,
    leverage: v.p.leverage,
    entryPx: v.p.entryPx,
    exitPx: v.mark,
    priceScale: scaleOf(markets, v.p.instrumentId),
    net: v.net,
    pct: v.roe,
    status: 'open',
  };
}

const W = 1200;
const H = 630;

const signedUsd = (net: bigint) => `${net < 0n ? '-' : '+'}$${fmtUsd(net < 0n ? -net : net)}`;
const signedPct = (pct: number) => `${pct >= 0 ? '+' : ''}${pct.toFixed(pct >= 1000 || pct <= -1000 ? 0 : 1)}%`;

function loadImage(src: string): Promise<HTMLImageElement | undefined> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(undefined);
    img.src = src;
  });
}

/** Draws the card on a 1200×630 canvas (the size X shows in full); always dark, whatever the page's theme */
async function draw(canvas: HTMLCanvasElement, c: PnlCardData): Promise<void> {
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d')!;
  await document.fonts?.ready;
  const [logo, mark] = await Promise.all([loadImage('/brand/logo-night.png'), loadImage('/brand/logo-mark.png')]);
  const win = c.net >= 0n;
  const hue = win ? '#22f05c' : '#ff3b47';

  // background: the app's night green, a glow on the side of the result
  g.fillStyle = '#040f0d';
  g.fillRect(0, 0, W, H);
  const glow = g.createRadialGradient(W * 0.78, H * 0.42, 20, W * 0.78, H * 0.42, 520);
  glow.addColorStop(0, win ? 'rgba(34, 240, 92, 0.20)' : 'rgba(255, 59, 71, 0.18)');
  glow.addColorStop(1, 'rgba(4, 15, 13, 0)');
  g.fillStyle = glow;
  g.fillRect(0, 0, W, H);
  g.strokeStyle = 'rgba(80, 227, 194, 0.06)';
  g.lineWidth = 1;
  for (let x = 0; x <= W; x += 60) {
    g.beginPath();
    g.moveTo(x + 0.5, 0);
    g.lineTo(x + 0.5, H);
    g.stroke();
  }
  for (let y = 0; y <= H; y += 60) {
    g.beginPath();
    g.moveTo(0, y + 0.5);
    g.lineTo(W, y + 0.5);
    g.stroke();
  }
  // the cat, large and faint, behind the result
  if (mark) {
    g.globalAlpha = 0.1;
    g.drawImage(mark, W - 470, H - 470, 520, 520);
    g.globalAlpha = 1;
  }

  const left = 72;
  if (logo) g.drawImage(logo, left, 58, 340, (340 * logo.height) / logo.width);
  else {
    g.fillStyle = '#e6fbf6';
    g.font = '700 40px Sora, sans-serif';
    g.fillText(config.appName, left, 100);
  }

  // market, side, leverage
  const sym = MARKETS[c.market]?.symbol ?? '?';
  g.font = '700 38px Sora, sans-serif';
  g.fillStyle = '#e6fbf6';
  g.fillText(`${sym}-PERP`, left, 200);
  const symW = g.measureText(`${sym}-PERP`).width;
  const tag = `${c.isLong ? 'LONG' : 'SHORT'} ${c.leverage}×`;
  g.font = '700 26px "IBM Plex Sans", sans-serif';
  const tagW = g.measureText(tag).width + 28;
  g.fillStyle = c.isLong ? 'rgba(34, 240, 92, 0.14)' : 'rgba(255, 59, 71, 0.15)';
  roundRect(g, left + symW + 20, 168, tagW, 42, 8);
  g.fill();
  g.fillStyle = c.isLong ? '#22f05c' : '#ff3b47';
  g.fillText(tag, left + symW + 34, 198);

  // the result: return on margin, then the dollars
  g.fillStyle = hue;
  g.font = '700 150px Sora, sans-serif';
  g.fillText(signedPct(c.pct), left - 6, 360);
  g.font = '600 52px "IBM Plex Sans", sans-serif';
  g.fillText(signedUsd(c.net), left, 432);

  // prices
  const label = (t: string, x: number) => {
    g.font = '600 22px "IBM Plex Sans", sans-serif';
    g.fillStyle = '#76998f';
    g.fillText(t, x, 506);
  };
  const value = (t: string, x: number) => {
    g.font = '600 34px "IBM Plex Sans", sans-serif';
    g.fillStyle = '#e6fbf6';
    g.fillText(t, x, 548);
  };
  label('Entry', left);
  value(fmtPx(c.entryPx, c.priceScale), left);
  const second = c.status === 'open' ? 'Mark' : c.status === 'liquidated' ? 'Liquidated at' : 'Exit';
  label(second, left + 260);
  value(fmtPx(c.exitPx, c.priceScale), left + 260);

  // where to trade
  g.font = '600 24px "IBM Plex Sans", sans-serif';
  g.fillStyle = '#50e3c2';
  g.textAlign = 'right';
  g.fillText(window.location.host, W - 64, H - 50);
  g.textAlign = 'left';
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

/** The card of `pnlCard` in a dialog: copy it as an image, download it, or open a post on X */
export function PnlCardModal() {
  const { pnlCard, openPnlCard } = useApp();
  if (!pnlCard) return null;
  return <PnlCardDialog card={pnlCard} onClose={() => openPnlCard(undefined)} />;
}

function PnlCardDialog({ card, onClose }: { card: PnlCardData; onClose: () => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [url, setUrl] = useState<string>();
  const [copied, setCopied] = useState<'ok' | 'fail'>();
  const dialog = useDialogFocus();
  useEscape(onClose);

  useEffect(() => {
    let live = true;
    void draw(canvas.current!, card).then(() => live && setUrl(canvas.current!.toDataURL('image/png')));
    return () => {
      live = false;
    };
  }, [card]);

  const blob = () => new Promise<Blob | null>((resolve) => canvas.current!.toBlob(resolve, 'image/png'));

  async function copy() {
    try {
      // a promise in the item keeps Safari's user-gesture window open while the PNG is encoded
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob().then((b) => b ?? Promise.reject(new Error('no image'))) })]);
      setCopied('ok');
    } catch {
      setCopied('fail');
    }
  }

  function download() {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${config.appName.toLowerCase()}-${MARKETS[card.market]?.symbol.toLowerCase() ?? 'pnl'}-${Date.now()}.png`;
    a.click();
  }

  const sym = MARKETS[card.market]?.symbol ?? '';
  const verb = card.status === 'open' ? 'up' : 'closed';
  const text =
    card.net >= 0n
      ? `${signedPct(card.pct)} on a ${card.leverage}× ${sym} ${card.isLong ? 'long' : 'short'}, ${verb} ${signedUsd(card.net)} on ${config.appName} 🐾`
      : `${signedPct(card.pct)} on a ${card.leverage}× ${sym} ${card.isLong ? 'long' : 'short'}. The cat always lands on its feet 🐾`;
  const share = `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.origin)}`;

  return createPortal(
    <div className="scrim" onClick={onClose}>
      <div className="modal pnl-modal" ref={dialog} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="PnL card">
        <header className="modal-head">
          <h3>Share your PnL</h3>
          <button className="x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <canvas ref={canvas} hidden />
        {url ? <img className="pnl-card-img" src={url} alt={text} width={W} height={H} /> : <div className="pnl-card-img pnl-card-wait" />}
        <div className="pnl-card-actions">
          <button className="btn btn-sm" disabled={!url} onClick={() => void copy()}>
            {copied === 'ok' ? 'Copied' : copied === 'fail' ? 'Copy failed: download it' : 'Copy Image'}
          </button>
          <button className="btn btn-sm" disabled={!url} onClick={download}>
            Download
          </button>
          <a className="btn btn-primary btn-sm" href={share} target="_blank" rel="noreferrer">
            Post on X
          </a>
        </div>
        <p className="desc">X does not take an image from a link: copy or download the card, then paste it into the post.</p>
      </div>
    </div>,
    document.body,
  );
}
